import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReportLayout from '../../components/reports/ReportLayout';
import FinancialReportSection, {
  FinancialReportRow
} from '../../components/reports/FinancialReportSection';
import { reportService } from '../../services/reportService';
import { formatCurrency } from '../../utils/formatters';
import {
  downloadBalanceSheetCsv,
  downloadBalanceSheetPdf
} from '../../utils/balanceSheetExport';
import SafeIcon from '../../common/SafeIcon';
import { useClient } from '../../context/ClientContext';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiDownload,
  FiExternalLink,
  FiPrinter,
  FiChevronDown
} = FiIcons;

const ReportRow = ({
  label,
  value,
  isTotal,
  isSubtotal,
  indent = 0,
  onClick
}) => (
  <div
    onClick={onClick}
    className={`flex items-center justify-between py-2.5 transition-colors ${
      onClick ? 'group cursor-pointer hover:bg-slate-50' : ''
    } ${
      isTotal
        ? 'mt-2 border-t-2 border-slate-900 font-black text-slate-900'
        : isSubtotal
          ? 'mt-1 border-t border-slate-200 font-bold text-slate-800'
          : 'border-b border-slate-50 text-slate-600'
    }`}
  >
    <div className="flex items-center gap-2">
      <span
        className={`text-[13px] ${
          indent > 0 ? `ml-${indent * 6}` : ''
        } ${label.includes('Less') ? 'italic text-rose-600' : ''}`}
      >
        {label}
      </span>
      {onClick && (
        <SafeIcon
          icon={FiExternalLink}
          className="text-[10px] text-indigo-500 opacity-0 group-hover:opacity-100"
        />
      )}
    </div>
    <span className={`text-[13px] font-black ${value < 0 ? 'text-rose-600' : ''}`}>
      {formatCurrency(value)}
    </span>
  </div>
);

const SectionHeader = ({ title }) => (
  <h3 className="mb-6 mt-10 rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">
    {title}
  </h3>
);

const SubHeader = ({ title }) => (
  <h4 className="mb-3 ml-2 mt-4 text-[10px] font-black uppercase tracking-widest text-slate-400">
    {title}
  </h4>
);

const CategoryRows = ({ categoryName, categoryData, showZeroBalances }) => {
  const accounts = (categoryData.accountDetails || []).filter((account) => (
    showZeroBalances || Number(account.amount || 0) !== 0
  ));

  if (accounts.length === 0 && Number(categoryData.total || 0) === 0) {
    return null;
  }

  return (
    <>
      <FinancialReportRow label={categoryName} value={categoryData.total} />
      {accounts.map((account) => (
        <FinancialReportRow
          key={account.id}
          label={account.code ? `${account.code} · ${account.name}` : account.name}
          value={account.amount}
          muted
        />
      ))}
      <FinancialReportRow
        label={`Total ${categoryName}`}
        value={categoryData.total}
        total
      />
    </>
  );
};

export default function BalanceSheetReport() {
  const navigate = useNavigate();
  const { activeClient } = useClient();
  const [basis, setBasis] = useState('accrual');
  const [asOfDate, setAsOfDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [viewMode, setViewMode] = useState('summary');
  const [showZeroBalances, setShowZeroBalances] = useState(false);
  const [openAccount, setOpenAccount] = useState(null);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await reportService.generateBalanceSheet(
        asOfDate,
        undefined,
        basis
      );
      setData(result);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [asOfDate, basis, activeClient?.id]);

  const updateReport = () => {
    setOpenAccount(null);
    fetchReport();
  };

  const isVisible = (value) => (
    showZeroBalances || Number(value || 0) !== 0
  );

  const handleExportCSV = () => {
    if (!data) return;

    downloadBalanceSheetCsv({
      companyName: activeClient?.legal_name || activeClient?.name || 'Accounting Workspace',
      asOfDate,
      basis,
      reportData: data,
      viewMode,
      showZeroBalances
    });
    setIsExportOpen(false);
  };

  const handleExportPDF = () => {
    if (!data) return;

    downloadBalanceSheetPdf({
      companyName: activeClient?.legal_name || activeClient?.name || '',
      asOfDate,
      basis,
      reportData: data,
      viewMode,
      showZeroBalances
    });
    setIsExportOpen(false);
  };

  const handleExport = (format) => {
    if (format === 'csv') {
      handleExportCSV();
    } else {
      handleExportPDF();
    }
  };

  const handleDrillDown = (account) => {
    if (account?.transactions) {
      setOpenAccount(openAccount === account.id ? null : account.id);
      return;
    }

    navigate(
      `/accounting/general-ledger?account=${encodeURIComponent(
        account?.name || account
      )}`
    );
  };

  const renderSection = (sectionName, title) => {
    const section = data[sectionName];
    const categories = Object.entries(section.categories || {});

    return (
      <FinancialReportSection
        title={title}
        totalLabel={`Total ${title}`}
        total={section.total}
        emptyLabel=""
        rows={
          viewMode === 'details' && (
            <>
              {categories.map(([categoryName, categoryData]) => (
                <CategoryRows
                  key={categoryName}
                  categoryName={categoryName}
                  categoryData={{
                    ...categoryData,
                    accountDetails: (categoryData.accountDetails || []).filter(
                      (account) => isVisible(account.amount)
                    )
                  }}
                  showZeroBalances={showZeroBalances}
                />
              ))}
            </>
          )
        }
      />
    );
  };

  const totalLiabilitiesAndEquity = data
    ? Number(data.liabilities.total || 0) + Number(data.equity.total || 0)
    : 0;

  void ReportRow;
  void SectionHeader;
  void SubHeader;
  void handleDrillDown;
  void updateReport;
  void FiAlertCircle;
  void FiDownload;
  void FiPrinter;
  void FiChevronDown;

  return (
    <ReportLayout
      title="Assets, Liabilities & Equity"
      description="Statement of Financial Position."
      viewMode={viewMode}
      setViewMode={setViewMode}
      endDate={asOfDate}
      onEndDateChange={setAsOfDate}
      onRefresh={fetchReport}
      loading={loading}
      onExport={handleExport}
      exportOpen={isExportOpen}
      setExportOpen={setIsExportOpen}
      basis={basis}
      setBasis={setBasis}
      showBasis
      asOfOnly
    >
      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <span>{error}</span>
          <button type="button" onClick={fetchReport} className="font-medium underline">
            Try again
          </button>
        </div>
      )}

      {data && (
        <div data-balance-sheet>
          {viewMode === 'details' && (
            <div className="mb-5 flex justify-end">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={showZeroBalances}
                  onChange={(event) => setShowZeroBalances(event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600"
                />
                Show Zero Balance Accounts
              </label>
            </div>
          )}

          {renderSection('assets', 'Assets')}
          {renderSection('liabilities', 'Liabilities')}
          {renderSection('equity', 'Equity')}

          <FinancialReportRow
            label="Total Liabilities & Equity"
            value={totalLiabilitiesAndEquity}
            total
            grand
          />

          {Math.abs(Number(data.assets.total || 0) - totalLiabilitiesAndEquity) >= 0.01 && (
            <p className="mt-4 text-xs text-amber-700">
              Balance verification variance:{' '}
              {formatCurrency(
                Math.abs(Number(data.assets.total || 0) - totalLiabilitiesAndEquity)
              )}
            </p>
          )}
        </div>
      )}

      {!loading && !data && !error && (
        <p className="py-10 text-center text-sm text-slate-500">
          No report data is available.
        </p>
      )}
    </ReportLayout>
  );
}